<script>
// Open ModalBox instances, oldest first — module-level so Escape reaches only
// the topmost one when dialogs stack (e.g. a confirm over a side drawer).
const openModals = []

// Open sheets (side drawers / bottom sheets) with their measured width. The
// Intercom launcher sits bottom-right, exactly where a right sheet's footer
// actions are (Trello YrT6kMPp): while one is open the launcher is moved left
// of the widest sheet (≥ lg) or hidden (bottom sheets below lg) — see the
// global style at the end of this file.
const openSheets = new Map()
const syncSheetMarker = () => {
	if (typeof document === 'undefined') return
	const body = document.body
	if (!openSheets.size) {
		delete body.dataset.sheetOpen
		body.style.removeProperty('--sheet-width')
		return
	}
	let placement = 'bottom'
	let width = 0
	for (const sheet of openSheets.values()) {
		if (sheet.placement === 'right') placement = 'right'
		width = Math.max(width, sheet.width || 0)
	}
	body.dataset.sheetOpen = placement
	if (width) body.style.setProperty('--sheet-width', `${Math.round(width)}px`)
	else body.style.removeProperty('--sheet-width')
}
</script>

<script setup>
import { computed, watch, useSlots, onBeforeUnmount, getCurrentInstance, nextTick, ref } from 'vue'
import Overlay from './Overlay.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCross from '@/components/icon/IconCross.vue'
import FormButton from '../Atoms/FormButton.vue'

const props = defineProps({
	title: {
		type: String
	},
	placement: {
		type: String,
		default: 'center'
	},
	bgColor: {
		type: String,
		default: 'bg-surface-2'
	},
	size: {
		type: String,
		default: 'md'
	},
	modelValue: {
		type: Boolean,
		default: false,
	},
	fullscreen: {
		type: Boolean,
		default: false,
	},
	hideClose: {
		type: Boolean,
		default: false,
	},
	zIndex: {
		type: String,
		default: 'z-50'
	},
	wrapperClasses: {
		type: String,
		default: ''
	},
	headerClass: {
		type: String,
		default: ''
	},
	bodyClasses: {
		type: String,
		default: '',
	},
	headerDivider: {
		type: Boolean,
		default: false,
	},
	footerDivider: {
		type: Boolean,
		default: false,
	},
	mobilePlacement: {
		type: String,
		default: 'center'
	},
	mobileHeightClass: {
		type: String,
		default: 'max-lg:max-h-[85dvh] max-lg:h-auto',
	},
	closeOnBackdrop: {
		type: Boolean,
		default: true,
	},
	// Opt-in: make the modal body a bounded, scrollable region so content below
	// the fold (lists, action buttons) stays reachable when the modal is height
	// capped (85vh centered / 90vh side) on mobile. Off by default to preserve
	// the previous layout for modals that don't need it.
	scrollBody: {
		type: Boolean,
		default: false,
	},
	// Opt-in: fill the viewport on mobile (full width + height). Desktop layout
	// (including side-sheet placement) is unchanged.
	mobileFullscreen: {
		type: Boolean,
		default: false,
	},
	// Escape closes the modal like the X button does (topmost open modal only).
	closeOnEscape: {
		type: Boolean,
		default: true,
	},
})

const emit = defineEmits({
	// falsy payloads (false on close) are valid; returning the value itself made Vue warn
	'update:modelValue': () => true,
	'close': () => true,
	'cancel': () => true,
	'open': () => true,
})

const slots = useSlots()
const hasFooterSlot = computed(() => Boolean(slots.footer))

const value = computed({
	get: () => props.modelValue,
	set: value => emit('update:modelValue', value),
})

const isMobileBottom = computed(() => props.mobilePlacement === 'bottom')
const isSidePlacement = computed(() => props.placement === 'left' || props.placement === 'right')

const overlayWrapperClass = computed(() => {
	if (isMobileBottom.value) {
		return 'max-lg:!items-end max-lg:!justify-center max-lg:!overflow-hidden'
	}
	if (props.placement === 'left') {
		return 'lg:!items-stretch lg:!justify-start max-lg:!items-end max-lg:!justify-center max-lg:!overflow-hidden'
	}
	if (props.placement === 'right') {
		if (props.mobileFullscreen) {
			return 'lg:!items-stretch lg:!justify-end max-lg:!items-stretch max-lg:!justify-stretch max-lg:!overflow-hidden'
		}
		return 'lg:!items-stretch lg:!justify-end max-lg:!items-end max-lg:!justify-center max-lg:!overflow-hidden'
	}
	if (props.mobileFullscreen) {
		return 'max-lg:!items-stretch max-lg:!justify-stretch max-lg:!overflow-hidden'
	}
	return ''
})

const contentTransition = computed(() => {
	if (isMobileBottom.value) {
		return {
			enterActive: 'modal-bottom-enter-active',
			enterFrom: 'modal-bottom-enter-from',
			enterTo: 'modal-bottom-enter-to',
			leaveActive: 'modal-bottom-leave-active',
			leaveFrom: 'modal-bottom-leave-from',
			leaveTo: 'modal-bottom-leave-to',
		}
	}

	if (props.placement === 'left') {
		return {
			enterActive: 'modal-left-enter-active',
			enterFrom: 'modal-left-enter-from',
			enterTo: 'modal-left-enter-to',
			leaveActive: 'modal-left-leave-active',
			leaveFrom: 'modal-left-leave-from',
			leaveTo: 'modal-left-leave-to',
		}
	}

	if (props.placement === 'right') {
		return {
			enterActive: 'modal-right-enter-active',
			enterFrom: 'modal-right-enter-from',
			enterTo: 'modal-right-enter-to',
			leaveActive: 'modal-right-leave-active',
			leaveFrom: 'modal-right-leave-from',
			leaveTo: 'modal-right-leave-to',
		}
	}

	return {
		enterActive: 'modal-content-enter-active',
		enterFrom: 'modal-content-enter-from',
		enterTo: 'modal-content-enter-to',
		leaveActive: 'modal-content-leave-active',
		leaveFrom: 'modal-content-leave-from',
		leaveTo: 'modal-content-leave-to',
	}
})

const overlayClick = () => {
	if (!props.closeOnBackdrop) return
	confirmCancel('cancel')
}

const confirmCancel = (mode) => {
	value.value = false
	emit(mode)
}

const close = () => confirmCancel('close')

// Calculate scrollbar width to prevent page bumping when modal opens
const getScrollbarWidth = () => {
	return window.innerWidth - document.documentElement.clientWidth
}

// Store original body padding-right to restore it later
let originalBodyPaddingRight = null

const lockBodyScroll = () => {
	// Calculate scrollbar width before hiding it
	const scrollbarWidth = getScrollbarWidth()

	// Store original padding-right if it exists
	const bodyStyle = window.getComputedStyle(document.body)
	originalBodyPaddingRight = bodyStyle.paddingRight

	document.body.classList.add('modalbox-open')

	// Add padding-right equal to scrollbar width to prevent page bumping
	if (scrollbarWidth > 0) {
		const currentPaddingRight = parseFloat(originalBodyPaddingRight) || 0
		document.body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`
	}
}

const unlockBodyScroll = () => {
	document.body.classList.remove('modalbox-open')
	if (originalBodyPaddingRight !== null) {
		document.body.style.paddingRight = originalBodyPaddingRight
	} else {
		document.body.style.paddingRight = ''
	}
	originalBodyPaddingRight = null
}

const computedClassStageOne = computed(() => {
	const colorClass = props.bgColor

	if (isSidePlacement.value) {
		const widthClass = (() => {
			switch (props.size) {
				case 'sm':
					return 'w-full lg:w-[480px]'
				case 'lg':
					return 'w-full lg:w-[640px]'
				case 'xl':
					return 'w-full lg:w-[640px] xl:w-[880px]'
				case 'md':
				default:
					return 'w-full lg:w-[510px]'
			}
		})()
		const positionClass = props.placement === 'left' ? 'lg:left-0' : 'lg:right-0'
		const mobileBottomClass = props.mobileFullscreen
			? 'max-lg:fixed max-lg:inset-0 max-lg:m-0 max-lg:w-full max-lg:h-dvh max-lg:max-w-none max-lg:max-h-none max-lg:rounded-none'
			: 'max-lg:fixed max-lg:inset-x-0 max-lg:bottom-0 max-lg:top-auto max-lg:m-0 max-lg:w-full max-lg:max-w-full max-lg:max-h-[90dvh] max-lg:rounded-t-2xl max-lg:rounded-b-none'
		const effectiveMobileHeight = props.mobileFullscreen ? '' : props.mobileHeightClass

		return [
			'z-30 modal-block flex flex-col min-h-0',
			colorClass,
			widthClass,
			positionClass,
			'lg:fixed lg:top-0 lg:bottom-0 lg:m-0 lg:h-full lg:max-h-full lg:rounded-none lg:overflow-visible',
			mobileBottomClass,
			effectiveMobileHeight,
		].join(' ')
	}

	const sizeClass = (() => {
		if (props.fullscreen) return 'w-screen h-dvh'
		switch (props.size) {
			case 'sm':
				return 'w-full lg:w-[480px]'
			case 'md':
				return 'w-full lg:w-[640px] lg:h-[480px] lg:max-h-[70vh] overflow-y-auto'
			case 'lg':
				return 'w-full lg:w-[1024px]'
			default:
				return 'w-full lg:w-[640px] lg:h-[480px]'
		}
	})()
	const baseClass = 'z-30 modal-block m-auto'
	const statusClass = value.value ? 'modal-open' : 'modal-closed'
	const mobileBottomClass = isMobileBottom.value
		? 'max-lg:fixed max-lg:inset-x-0 max-lg:bottom-0 max-lg:top-auto max-lg:m-0 max-lg:w-full max-lg:max-w-full max-lg:rounded-t-2xl max-lg:rounded-b-none max-lg:shadow-lg max-lg:flex max-lg:flex-col max-lg:min-h-0'
		: ''

	const centerMobileWidth = isMobileBottom.value
		? ''
		: 'max-lg:max-w-[calc(100vw_-_2rem)] max-lg:flex max-lg:flex-col max-lg:min-h-0 max-lg:overflow-hidden'

	return [baseClass, colorClass, sizeClass, statusClass, mobileBottomClass, centerMobileWidth, props.mobileHeightClass].join(' ')
})

const mainClasses = computed(() => {
	if (isSidePlacement.value) {
		// Side sheets always scroll on mobile; desktop keeps the previous
		// opt-in behavior for layouts that manage their own scrolling.
		return props.scrollBody
			? 'flex-1 min-h-0 relative overflow-y-auto [scrollbar-gutter:stable]'
			: 'flex-1 min-h-0 relative max-lg:overflow-y-auto lg:overflow-hidden [scrollbar-gutter:stable]'
	}
	// Mobile bottom-sheets already scroll; other modals opt in via scroll-body so
	// content below the fold stays reachable under the height cap.
	// scrollbar-gutter keeps content width stable when the scrollbar toggles
	// (e.g. switching tabs with different content heights).
	if (isMobileBottom.value || props.scrollBody) {
		return 'h-full min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]'
	}
	return 'h-full max-lg:min-h-0 max-lg:flex-1 max-lg:overflow-y-auto [scrollbar-gutter:stable]'
})

// --- keyboard + focus --------------------------------------------------------------
// One document listener per open modal; only the topmost (last opened) one
// reacts to Escape, so a confirm stacked over a drawer closes first. Escape is
// left alone while typing in a field — inline editors (rename inputs, pickers)
// use it to cancel their own edit — and when something already handled it.
const uid = getCurrentInstance()?.uid ?? Symbol('modal')
const TEXT_ENTRY = 'textarea, select, [contenteditable]:not([contenteditable="false"]), input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="range"])'
let opener = null

const onKeydown = (event) => {
	if (event.key !== 'Escape' || event.defaultPrevented) return
	if (!props.closeOnEscape || !value.value) return
	if (openModals[openModals.length - 1] !== uid) return
	if (event.target instanceof Element && event.target.closest(TEXT_ENTRY)) return
	event.preventDefault()
	close()
}

const register = () => {
	if (openModals.includes(uid)) return
	// remember who opened us before the overlay moves focus inside
	opener = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null
	openModals.push(uid)
	document.addEventListener('keydown', onKeydown)
}

const unregister = () => {
	const i = openModals.indexOf(uid)
	if (i !== -1) openModals.splice(i, 1)
	document.removeEventListener('keydown', onKeydown)
}

// Runs after the leave transition: focus goes back to the element that opened
// the modal unless the user (or the app) already moved it somewhere else.
const restoreFocus = () => {
	const target = opener
	opener = null
	const active = document.activeElement
	if (!target || !target.isConnected || (active && active !== document.body && document.body.contains(active))) return
	target.focus({ preventScroll: true })
}

const onAfterLeave = () => {
	unlockBodyScroll()
	restoreFocus()
}

// Side drawers and bottom sheets: publish their presence (and width) on
// <body> so fixed helpers such as the Intercom launcher can step aside.
const dialogEl = ref(null)
let sheetObserver = null
const isSheet = computed(() => isSidePlacement.value || isMobileBottom.value)
const trackSheet = async () => {
	if (!isSheet.value || typeof document === 'undefined') return
	openSheets.set(uid, { placement: props.placement === 'right' ? 'right' : props.placement === 'left' ? 'left' : 'bottom', width: 0 })
	syncSheetMarker()
	await nextTick()
	const el = dialogEl.value
	if (!el || typeof ResizeObserver !== 'function') return
	sheetObserver?.disconnect()
	sheetObserver = new ResizeObserver(() => {
		const sheet = openSheets.get(uid)
		if (!sheet) return
		sheet.width = el.getBoundingClientRect().width
		syncSheetMarker()
	})
	sheetObserver.observe(el)
}
const untrackSheet = () => {
	sheetObserver?.disconnect()
	sheetObserver = null
	if (openSheets.delete(uid)) syncSheetMarker()
}

// Lock the body scroll as soon as the modal opens. The unlock is deferred to
// the overlay's after-leave hook so the scrollbar (and its width compensation)
// only returns once the close animation has finished — otherwise the page and
// the still-animating modal shift sideways mid-transition.
watch(value, () => {
	if (value.value) {
		lockBodyScroll()
		register()
		trackSheet()
	} else {
		unregister()
		untrackSheet()
	}
}, { immediate: true })

onBeforeUnmount(() => {
	unregister()
	untrackSheet()
	unlockBodyScroll()
})

</script>

<template>
	<Teleport to="body">
		<Transition enter-active-class="modal-enter-active" enter-from-class="modal-enter-from"
			enter-to-class="modal-enter-to" leave-active-class="modal-leave-active" leave-from-class="modal-leave-from"
			leave-to-class="modal-leave-to" @after-leave="onAfterLeave">
			<overlay
				v-if="value"
				:z-index="zIndex"
				:wrapper-class="overlayWrapperClass"
				:disable-slot-transition="true"
				@overlay-click="overlayClick"
			>
				<Transition
					:enter-active-class="contentTransition.enterActive"
					:enter-from-class="contentTransition.enterFrom"
					:enter-to-class="contentTransition.enterTo"
					:leave-active-class="contentTransition.leaveActive"
					:leave-from-class="contentTransition.leaveFrom"
					:leave-to-class="contentTransition.leaveTo"
				>
					<div
						v-if="value"
						ref="dialogEl"
						role="dialog"
						aria-modal="true"
						:aria-label="props.title || undefined"
						class="rounded-md relative flex flex-col modal-content"
						:class="[
							computedClassStageOne,
							wrapperClasses,
							isMobileBottom ? 'modal-content-bottom' : '',
							isSidePlacement ? 'modal-content-side' : '',
						]"
					>
						<header :class="['w-full', 'flex', 'shrink-0', props.headerClass, 'p-4 sm:p-6', isMobileBottom ? 'max-lg:rounded-t-2xl' : '']" v-if="!props.fullscreen">
							<h2 class="min-w-0 text-lg font-medium break-words">{{ props?.title }}</h2>
							<slot name="header" />
							<FormButton class="ml-auto z-10" v-if="!props.hideClose" @click="close" type="link" isIcon size="sm">
								<icon-base fill="transparent">
									<icon-cross />
								</icon-base>
							</FormButton>
						</header>
						<main :class="[mainClasses, bodyClasses]">
							<slot />
						</main>
						<footer v-if="!props.fullscreen && hasFooterSlot" class="shrink-0 mt-auto" :class="props.footerDivider ? 'border-t border-surface-3' : ''">
							<slot name="footer" />
						</footer>
					</div>
				</Transition>
			</overlay>
		</Transition>
	</Teleport>
</template>

<style scoped>
.modal-block {
	@apply shadow-md;
}

.modal-content-bottom {
	transition: none !important;
}

.modal-content-side {
	transition: none !important;
}

@media (max-width: 1023px) {
	.modal-block.modal-content-bottom {
		@apply transition-none;
	}
}

/* Base Modal Transitions */
/* Keep a transition on the overlay root so Vue can time enter/leave;
   the visible animation happens on the backdrop and the modal content. */
.modal-enter-active {
	transition: opacity 240ms ease-out;
}

.modal-leave-active {
	transition: opacity 200ms ease-in;
}

/* Fading the overlay's opacity makes backdrop-filter pop in/out at full
   strength, so ramp the blur amount and dim color directly. Keyframe
   animations are used because class-removal transitions on
   backdrop-filter can get stuck at the start value (style-flush race). */
.modal-enter-active :deep(.overlay-backdrop) {
	animation: modal-backdrop-in 240ms ease-out both;
}

.modal-leave-active :deep(.overlay-backdrop) {
	animation: modal-backdrop-out 200ms ease-in both;
}

@keyframes modal-backdrop-in {
	from {
		background-color: transparent;
		-webkit-backdrop-filter: blur(0px);
		backdrop-filter: blur(0px);
	}
	to {
		background-color: rgb(0 0 0 / 0.45);
		-webkit-backdrop-filter: blur(4px);
		backdrop-filter: blur(4px);
	}
}

@keyframes modal-backdrop-out {
	from {
		background-color: rgb(0 0 0 / 0.45);
		-webkit-backdrop-filter: blur(4px);
		backdrop-filter: blur(4px);
	}
	to {
		background-color: rgb(0 0 0 / 0);
		-webkit-backdrop-filter: blur(0px);
		backdrop-filter: blur(0px);
	}
}

/* Center Modal Content Transitions */
.modal-content {
	will-change: transform, opacity;
}

/* Content settles in slightly slower with a soft ease-out; leaves a touch
   faster than the overlay (200ms) so it finishes before the backdrop unmounts. */
.modal-content-enter-active {
	transition: opacity 240ms ease-out, transform 240ms cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-content-leave-active {
	transition: opacity 180ms ease-in, transform 180ms cubic-bezier(0.4, 0, 1, 1);
}

.modal-content-enter-from,
.modal-content-leave-to {
	opacity: 0;
	transform: scale(0.97);
}

.modal-content-enter-to,
.modal-content-leave-from {
	opacity: 1;
	transform: scale(1);
}

/* Bottom sheet transitions (mobile) */
@media (max-width: 1023px) {
	.modal-bottom-enter-active {
		transition: transform 450ms cubic-bezier(0.22, 1, 0.36, 1);
	}

	.modal-bottom-leave-active {
		transition: transform 320ms cubic-bezier(0.4, 0, 1, 1);
	}

	.modal-bottom-enter-from,
	.modal-bottom-leave-to {
		opacity: 1;
		transform: translate3d(0, 100%, 0);
	}

	.modal-bottom-enter-to,
	.modal-bottom-leave-from {
		opacity: 1;
		transform: translate3d(0, 0, 0);
	}
}

@media (min-width: 1024px) {
	.modal-bottom-enter-active,
	.modal-bottom-leave-active {
		transition: opacity 200ms cubic-bezier(0.4, 0, 0.2, 1), transform 200ms cubic-bezier(0.4, 0, 0.2, 1);
	}

	.modal-bottom-enter-from,
	.modal-bottom-leave-to {
		opacity: 0;
		transform: scale(0.95);
	}

	.modal-bottom-enter-to,
	.modal-bottom-leave-from {
		opacity: 1;
		transform: scale(1);
	}
}

/* For right placement animations */
.modal-right-enter-active {
	transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-right-leave-active {
	transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-right-enter-from {
	opacity: 0;
	transform: translateY(100%);
}

@media (min-width: 768px) {
	.modal-right-enter-from {
		transform: translateX(100%);
	}
}

.modal-right-enter-to,
.modal-right-leave-from {
	opacity: 1;
	transform: translate(0);
}

.modal-right-leave-to {
	opacity: 0;
	transform: translateY(100%);
}

@media (min-width: 768px) {
	.modal-right-leave-to {
		transform: translateX(100%);
	}
}

/* For left placement animations */
.modal-left-enter-active {
	transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-left-leave-active {
	transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-left-enter-from {
	opacity: 0;
	transform: translateX(-100%);
}

@media (max-width: 1023px) {
	.modal-left-enter-from,
	.modal-left-leave-to {
		transform: translate3d(0, 100%, 0);
	}
}

.modal-left-enter-to,
.modal-left-leave-from {
	opacity: 1;
	transform: translateX(0);
}

.modal-left-leave-to {
	opacity: 0;
	transform: translateX(-100%);
}
</style>

<style>
.modalbox-open {
	@apply bg-surface-1
}

body.modalbox-open {
	overflow-y: hidden;
}
</style>

<style>
/* Keep the Intercom launcher clear of an open sheet's footer actions (Trello
   YrT6kMPp): left of a right-hand drawer from lg up, hidden under a bottom
   sheet below lg. Intercom regenerates its hashed classes, so match its stable
   iframe names and the wrappers that hold them. `--sheet-width` is measured by
   ModalBox (`syncSheetMarker`). */
@media (min-width: 1024px) {
	body[data-sheet-open="right"] :is(
		.intercom-lightweight-app-launcher,
		.intercom-lightweight-app-messenger,
		iframe[name="intercom-launcher-frame"],
		div:has(> iframe[name="intercom-launcher-frame"]),
		div:has(> iframe[name="intercom-messenger-frame"])
	) {
		right: calc(var(--sheet-width, 640px) + 20px) !important;
	}
}

@media (max-width: 1023px) {
	body[data-sheet-open] :is(
		.intercom-lightweight-app-launcher,
		iframe[name="intercom-launcher-frame"],
		div:has(> iframe[name="intercom-launcher-frame"])
	) {
		display: none !important;
	}
}
</style>
